import { Chip, tooltipClasses } from "@mui/material";
import { GridApiCommon, GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { createElement } from "react";
import { TbVip } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import {
    PassengerTransferType,
    passengerTransferTypeOptions,
    passengerTransferTypes,
} from "@/constants/passengerTransferTypes";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import { usePlanActions } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";

import MemoizedEditAutoComplete from "./EditAutoCompletete";
import MemoizedEditableColumn from "./EditableColumn";

const getTransferType = (value: PassengerTransferType | string) =>
    passengerTransferTypes.find((item) => item.value == value || (typeof value === "string" && item.label === value));

function PassengerTransferTypeCell({ value }: { value: PassengerTransferType | string }) {
    const status = getTransferType(value);

    return (
        (status && (
            <div className="flex items-center  h-full">
                {
                    <NoMaxWidthTooltip
                        arrow
                        sx={{
                            [`& .${tooltipClasses.tooltip}`]: {
                                backgroundColor: "white",
                                border: "none",
                                padding: "0px !important",
                                fontSize: 11,
                                maxWidth: "240px !important",
                            },
                        }}
                        title={<HotelStatusTooltip status={status} />}
                    >
                        <Chip
                            sx={{
                                backgroundColor: status.bg,
                                color: status.text,
                                fontWeight: 500,
                                fontSize: "12px !important",
                            }}
                            icon={createElement(status.component(), {
                                fontSize: 14,
                                color: status.text,
                            })}
                            label={status.label}
                        />
                    </NoMaxWidthTooltip>
                }
            </div>
        )) ||
        "-"
    );
}

function EditableTransferCell({
    value,
    row,
    field,
    api,
}: {
    api: GridApiCommon;
    field: string;
    row: IPlanPassenger<"api">;
    value: PassengerTransferType;
}) {
    return (
        <MemoizedEditableColumn
            renderHistoryComp={(val) =>
                val ? <PassengerTransferTypeCell value={val as PassengerTransferType} /> : <span>-</span>
            }
            api={api}
            field={field}
            id={row.id}
            editable
        >
            <PassengerTransferTypeCell value={value} />
        </MemoizedEditableColumn>
    );
}

function EditCell({
    value,
    row,
    field,
    api,
}: {
    api: GridApiCommon;
    field: string;
    row: IPlanPassenger<"api">;
    value: PassengerTransferType;
}) {
    const { processRowUpdate } = usePlanActions();
    return (
        <MemoizedEditAutoComplete
            processRowUpdate={processRowUpdate}
            placeholder="transferType"
            icon={TbVip}
            api={api}
            row={row}
            field={field}
            value={value}
            options={passengerTransferTypeOptions}
            optionComp={TransferOption}
            renderHistoryComp={(val) =>
                val ? <PassengerTransferTypeCell value={val as PassengerTransferType} /> : <span>-</span>
            }
        />
    );
}

function TransferOption({ option }: { option: { label: string; value: string | number } }) {
    return <PassengerTransferTypeCell value={option.value as PassengerTransferType} />;
}

const PassengerTransferTypeColumn = ({ t }: { t: TFunction }): GridColDef<IPlanPassenger<"api">> => {
    return {
        headerName: t("transferType"),
        field: "transferType",
        cellClassName: "!px-1",
        minWidth: 200,
        sortable: true,
        disableColumnMenu: true,
        editable: true,
        valueGetter: (_, row) => row.transferType || PassengerTransferType.STANDARD,
        pastedValueParser: (value: string) => {
            const status = getTransferType(value);

            return (status?.value && status.value.toString()) || undefined;
        },
        renderHeader: (params) => <TableHeader Component={TbVip} title={params.colDef.headerName!} />,
        renderCell: ({ value, api, row, field }) => (
            <EditableTransferCell api={api} field={field} row={row} value={value} />
        ),
        renderEditCell: ({ value, api, row, field }) => <EditCell api={api} field={field} row={row} value={value} />,
    };
};

export default PassengerTransferTypeColumn;
